import { Box, Card, CircularProgress, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { getMappingStatus } from "routes/asset-register/plugins/asset-mapping/controller/get-mapping-status"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { RegisterMapping, RegisterTypeProgress } from "event-definitions"
import { uniqueId } from "library/database/split-id.mjs"
import {
    globalMappingStatus,
    globalRefiningStatus,
} from "routes/asset-register/plugins/asset-mapping/global-mapping-status"
import {
    RegisterMappingUpdated,
    RegisterRefiningUpdated,
} from "routes/asset-register/plugins/asset-mapping/register-mapping-updated"
import { ListItemBox } from "lib/@components/ListItemBox"
import { RegisterTitleClient } from "routes/asset-register/asset-register"
import { createEvent } from "library/local-events/index.mjs"
import { useIsAnythingBeingMapped } from "routes/asset-register/plugins/asset-mapping/use-is-anything-being-mapped"
import { getRegisterProgress } from "routes/asset-register/plugins/asset-mapping/controller/get-register-progress"
import { deriveShortGuidFrom } from "library/guid/index.mjs"
import { refreshId } from "lib/graphql/api"
import { successSnackbar } from "lib/snackbar/success-snackbar"

export const StatusDisplay = createEvent("StatusDisplay")

export function CurrentMappingStatus() {
    const { register } = useBoundContext()
    const isMapping = useIsAnythingBeingMapped()
    const [mappingStatus, setMappingStatus] = useState([])
    const [mappingTotal, setMappingTotal] = useState(0)
    const [refiningStatus, setRefiningStatus] = useState([])
    const { mappingKeys, length } = getMappingStatus.useResults(refreshId(register?._id)) ?? {}
    const registerProgress = getRegisterProgress.useResults(refreshId(register?.id))

    useEffect(() => {
        setMappingStatus(mappingKeys)
        setMappingTotal(length)
    }, [mappingKeys, length])

    useEffect(() => {
        setRefiningStatus(registerProgress)
    }, [registerProgress])

    useEffect(() => {
        if (register) {
            globalMappingStatus[uniqueId(register._id)] = mappingStatus
            RegisterMappingUpdated(uniqueId(register._id)).raise(mappingStatus)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deriveShortGuidFrom(register, mappingStatus)])

    useEffect(() => {
        if (register) {
            globalRefiningStatus[uniqueId(register._id)] = refiningStatus
            RegisterRefiningUpdated(uniqueId(register._id)).raise(refiningStatus)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deriveShortGuidFrom(register, refiningStatus)])

    RegisterMapping.useEvent(
        (registerId, status, total) => {
            if (register?._id === registerId) {
                setMappingStatus(status)
                setMappingTotal(total)
            }
        },
        [register]
    )

    RegisterTypeProgress.useEvent(
        (registerId, lastBlockSize, remaining, total) => {
            if (register?._id === registerId) {
                setRefiningStatus({ lastBlockSize, remaining, total })
            }
        },
        [register]
    )

    const currentMapped = mappingTotal > 0 ? Math.max(0, mappingTotal - (mappingStatus?.length ?? 0)) : 0
    const currentProgress = currentMapped / mappingTotal
    const display = isMapping && currentProgress < 0.999

    const mappingRun = useRef({ registerId: null, started: false })

    useEffect(() => {
        const registerId = register?._id
        if (mappingRun.current.registerId !== registerId) {
            mappingRun.current = { registerId, started: false }
            return
        }
        if (display && currentProgress > 0) {
            mappingRun.current.started = true
            return
        }
        if (!display && mappingRun.current.started && currentProgress >= 0.999) {
            mappingRun.current.started = false
            successSnackbar("All Mapping Refreshed")
        }
    }, [register?._id, display, currentProgress])

    useEffect(() => {
        StatusDisplay.raiseLater(display)
    }, [display])

    return display ? (
        <RegisterTitleClient>
            <Card sx={{ position: "absolute", right: 0, top: -8 }}>
                <Box sx={{ py: 1, pl: 1, pr: 2, width: "max-content" }}>
                    <ListItemBox spacing={2}>
                        <CircularProgress size={20} color="primary" />
                        <Typography variant="body2" data-cy="ai-mapping-status-bar">
                            <strong>
                                {currentMapped} of {mappingTotal} asset types mapped
                            </strong>
                        </Typography>
                    </ListItemBox>
                </Box>
            </Card>
        </RegisterTitleClient>
    ) : null
}
